/**
 * Shared useAuth hook for Entra External ID (CIAM)
 *
 * Fixes applied vs original web/admin implementations:
 * 1. Always returns idToken (not accessToken) — CIAM ID tokens have aud=clientId
 * 2. Fallback is always acquireTokenRedirect (not popup — popups get blocked)
 * 3. inProgress guard prevents concurrent MSAL interactions
 * 4. Token caching avoids repeated acquireTokenSilent iframe calls
 */
import { useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useCallback, useMemo, useRef } from 'react';
import { loginRequest } from './config';
import type { AuthUser, UseAuthOptions, UseAuthReturn } from './types';

const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 min before expiry

interface CachedToken {
  token: string;
  expiresAt: number; // ms since epoch
}

/**
 * Parse JWT exp claim without a library.
 * Returns expiry as ms since epoch, or 0 if unparseable.
 */
function getTokenExpiry(token: string): number {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return 0;
    const payload = JSON.parse(atob(parts[1]!.replaceAll('-', '+').replaceAll('_', '/')));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

function buildUser(
  account: NonNullable<ReturnType<typeof useAccount>>,
  isStaffCheck?: (email: string) => boolean
): AuthUser & { isAuthorized?: boolean } {
  const claims = (account.idTokenClaims ?? {}) as Record<string, unknown>;
  const roles = (claims.roles ?? claims.extension_Role ?? []) as string[];
  const isAdmin = roles.includes('admin') || roles.includes('Admin');

  const isStaff = isStaffCheck
    ? isStaffCheck(account.username)
    : roles.includes('staff') || roles.includes('Staff') || isAdmin;

  const result: AuthUser & { isAuthorized?: boolean } = {
    id: account.localAccountId,
    email: account.username,
    name: account.name ?? account.username,
    roles,
    isAdmin,
    isStaff,
    claims,
  };

  if (isStaffCheck) {
    result.isAuthorized = isStaff;
  }

  return result;
}

export function useAuth(options?: UseAuthOptions): UseAuthReturn {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] ?? null);
  const isLoading = inProgress !== InteractionStatus.None;
  const tokenCache = useRef<CachedToken | null>(null);

  const login = useCallback(async () => {
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(async () => {
    tokenCache.current = null;
    await instance.logoutRedirect({ postLogoutRedirectUri: '/' });
  }, [instance]);

  const getAccessToken = useCallback(async () => {
    if (!account) return null;

    const cached = tokenCache.current;
    if (cached && Date.now() < cached.expiresAt - TOKEN_REFRESH_BUFFER_MS) {
      return cached.token;
    }

    try {
      const response = await instance.acquireTokenSilent({ ...loginRequest, account });
      const token = response.idToken;
      tokenCache.current = { token, expiresAt: getTokenExpiry(token) };
      return token;
    } catch {
      if (inProgress === InteractionStatus.None) {
        try { await instance.acquireTokenRedirect(loginRequest); } catch { /* redirect navigates away */ }
      }
      return null;
    }
  }, [instance, account, inProgress]);

  const user = useMemo(
    () => (account ? buildUser(account, options?.isStaffCheck) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-compute when account or isStaffCheck identity changes
    [account, options?.isStaffCheck],
  );

  return { isAuthenticated, isLoading, user, account, login, logout, getAccessToken };
}
